/**
 * Pure, React-free trial-countdown math for the in-app trial badge (PR-C3).
 *
 * `daysLeft` returns whole UTC calendar days from `now` until `end` (exclusive
 * of the end instant). A brand-new 14-day auto-trial therefore reads "14 days
 * left", not 15 (ceil on a 14d + ε window used to inflate by one). Returns
 * `null` when `end` is missing or unparseable, and `0` once the trial end is
 * at/in the past (callers render the "ends today / expired" escalation at 0).
 */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfUtcDay(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function daysLeft(
  now: Date | number,
  end: Date | number | string | null
): number | null {
  if (end === null || end === undefined) return null;

  const endMs =
    end instanceof Date
      ? end.getTime()
      : typeof end === 'number'
        ? end
        : Date.parse(end);
  if (Number.isNaN(endMs)) return null;

  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  if (Number.isNaN(nowMs)) return null;

  // Past end → 0 (never negative).
  if (endMs <= nowMs) return 0;

  const diffDays = (startOfUtcDay(endMs) - startOfUtcDay(nowMs)) / MS_PER_DAY;
  return Math.max(0, Math.floor(diffDays));
}
